import { useQuery } from "@tanstack/react-query";
import { format, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { DateRange } from "react-day-picker";
import { boletosApi, BoletoLine, BoletoTransaction, FinancialSummary } from "@/lib/api";
import { boletoMOCK } from "@/lib/MOCK/boleto";

/** `parseISO` lê "yyyy-MM-dd" no fuso local; `new Date` leria em UTC e jogaria o dia 1º para o mês anterior. */
function parseApiDate(value: string): Date {
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : new Date(value);
}

export interface FinancialDataResult {
    customerTransactionsList: BoletoTransaction[];
    summary: FinancialSummary;
}

/**
 * Busca e consolida os dados financeiros de um cliente:
 * - Transações principais (getFinancialDetails)
 * - Devoluções/retornos (getReturnTransactions)
 * - Transações liquidadas (getClosedTransactions)
 *
 * @param cnpj  CNPJ do cliente
 * @param date  Período de busca (from / to)
 */
export function useFinancialData(cnpj: string | undefined, date: DateRange | undefined) {
    return useQuery<FinancialDataResult>({
        queryKey: ['getBoleto', cnpj, date],
        queryFn: async () => {
            if (!cnpj || !date?.from || !date?.to) {
                return {
                    customerTransactionsList: [],
                    summary: { total: 0, liquidado: 0, vencidos: 0, emAberto: 0 },
                };
            }

            const dataDto = {
                from: format(date.from, 'yyyy-MM-dd', { locale: ptBR }),
                to: format(date.to, 'yyyy-MM-dd', { locale: ptBR }),
            };

            const [financialResponse, returnsResponse, boletosResponse] = await Promise.all([
                boletosApi.getFinancialDetails(cnpj, dataDto),
                boletosApi.getReturnTransactions(cnpj, dataDto),
                boletosApi.getClosedTransactions(cnpj, dataDto),
            ]);

            const responseData = financialResponse.data;
            const linsArray = responseData.customerTransactionsList.flatMap(e => e.lines);

            // --- Devoluções / Retornos ---
            // O mês da fatura vem do dueDate das lines; o transDate é só a data em que a operação foi feita.
            const returnsTransactions: BoletoTransaction[] = returnsResponse.data.customerTransactionsList?.map(row => {
                const dueDate = parseApiDate(row.lines?.[0]?.dueDate || row.transDate);

                const lines: BoletoLine[] = (row.lines || []).map(line => ({
                    $id: line.$id,
                    installments: "",
                    dueDate: `${line.dueDate ? parseApiDate(line.dueDate) : dueDate}`,
                    transDate: `${parseApiDate(line.transDate || row.transDate)}`,
                    orderId: line.orderId || "",
                    invoiceId: (line as any).invoiceId,
                    oldAmount: line.amount,
                    newAmount: 0,
                    paymentStatus: 0,
                    transactionType: 0,
                    typeLabel: row.paymentStatus === 2 ? 'Discount' : "Return",
                    salesOrderId: "",
                    magentoOrderId: line.orderId || "",
                    dynamicsSalesId: "",
                    orderNumber: "",
                    isIsencao: false,
                    isDevolucao: true,
                }));

                return {
                    $id: row.$id,
                    cnpjCpf: cnpj,
                    monthYear: `${dueDate.getMonth() + 1}/${dueDate.getFullYear()}`,
                    dueDate: `${dueDate}`,
                    oldAmount: row.amount,
                    newAmount: 0,
                    paymentStatus: row.paymentStatus,
                    statusLabel: "Pago",
                    hasCorrection: false,
                    hasBoleto: false,
                    parmJournalTransRecId: 0,
                    lines,
                };
            }) || [];

            // --- Transações liquidadas ---
            const closedTransactions: BoletoTransaction[] = boletosResponse.data.customerTransactionsClosedList?.map(row => {
                const line = linsArray.find(e => e.invoiceId === row.invoiceId);

                return {
                    $id: row.$id,
                    cnpjCpf: cnpj,
                    monthYear: row.monthYear,
                    dueDate: row.dueDate,
                    oldAmount: row.amount,
                    newAmount: 0,
                    paymentStatus: 0,
                    statusLabel: "Pago",
                    hasCorrection: false,
                    hasBoleto: false,
                    parmJournalTransRecId: 0,
                    lines: [{
                        $id: row.$id,
                        orderId: line?.orderId || row.OrderNumber || "",
                        orderNumber: line?.orderNumber || row.OrderNumber || "",
                        dynamicsSalesId: line?.dynamicsSalesId ?? "",
                        salesOrderId: line?.salesOrderId || "",
                        magentoOrderId: line?.magentoOrderId || "",
                        invoiceId: row.invoiceId,
                        installments: "",
                        oldAmount: row.amount,
                        newAmount: 0,
                        paymentStatus: 0,
                        transactionType: 0,
                        isIsencao: row.isIsencao,
                        isDevolucao: row.isDevolucao,
                        dueDate: row.dueDate,
                        transDate: row.settleDate,
                        typeLabel: row.isDevolucao ? "Return" : row.invoiceId?.includes("AC") ? "Acordo" : row.isIsencao ? "Discount" : "Pedido",
                    }],
                };
            }) || [];

            const totalClosed = closedTransactions.reduce((acc, curr) => acc + curr.oldAmount, 0);

            return {
                customerTransactionsList: [
                    ...responseData.customerTransactionsList,
                    ...returnsTransactions,
                    ...closedTransactions,
                ],
                summary: {
                    ...responseData.summary,
                    liquidado: (responseData.summary.liquidado || 0) + totalClosed,
                    total: (responseData.summary.total || 0) + totalClosed,
                },
            };
        },
        enabled: !!cnpj && !!date?.from && !!date?.to,
        retry: false,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: true,
    });
}
